import { useState } from "react";
import { useForm } from "../../hooks/useForm";
import Section from "../layout/Section";
import InfoTooltip from "../layout/InfoTooltip";
import {
  SHINGLE_COLORS,
  SHINGLE_BRANDS,
  SHINGLE_TYPES,
} from "../../utils/constants";

export default function ShinglesSection() {
  const {
    materialType,
    metalColor,
    shingleType,
    shingleColor,
    shingleColorOther,
    shingleBrand,
    shingleBrandOther,
    setMaterialType,
    setField,
    setShingleType,
    setShingleColor,
    setShingleBrand,
  } = useForm();

  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);

  const sectionTooltip = (
    <>
      In this section you select the type of cover the roof of your structure
      will have. Choosing this information correctly guarantees the plan
      includes clear specifications of the cover type you'll use.
    </>
  );

  const getColorImage = (color: string, type: string) => {
    const folder =
      type === SHINGLE_TYPES.ARCHITECTURAL ? "architectural" : "3-tab";
    return `/images/shingle-colors/${folder}/${color.toLowerCase()}-${folder === "architectural" ? "architectural" : "3tab"}.jpeg`;
  };

  return (
    <Section title="Shingles" tooltip={sectionTooltip}>
      <div className="mt-6 mb-3">
        <h3 className="font-medium mb-3">Type of material</h3>
        <div className="flex flex-col gap-2 md:flex-row md:justify-center md:gap-4">
          <label className="inline-flex justify-center items-center">
            <input
              type="radio"
              name="materialType"
              checked={materialType === "metal"}
              onChange={() => setMaterialType("metal")}
            />
            <span className="ml-1">Metal</span>
          </label>
          <label className="inline-flex justify-center items-center">
            <input
              type="radio"
              name="materialType"
              checked={materialType === "shingles"}
              onChange={() => setMaterialType("shingles")}
            />
            <span className="ml-1">Shingles</span>
          </label>
        </div>
      </div>

      {materialType === "metal" && (
        <div className="mt-4">
          <input
            type="text"
            value={metalColor}
            onChange={(e) => setField("metalColor", e.target.value)}
            placeholder="Type the chosen color"
            className="ml-3 border-2 border-gray-300 rounded px-2 py-1 w-full md:w-1/3 capitalize-input"
          />
          <InfoTooltip>
            Ask your designer or constructor for the exact color
          </InfoTooltip>
        </div>
      )}

      {materialType === "shingles" && (
        <div className="mt-4">
          <div className="mt-6 mb-3">
            <h3 className="font-medium mb-3">
              Type
              <InfoTooltip>
                Select the shingle type you're going to have depending on what
                your builder indicates, and according to the type we give you
                options to select the color and if it's not among the options,
                enter the one you like.
              </InfoTooltip>
            </h3>
            <div className="flex flex-col gap-2 md:flex-row md:justify-center md:gap-4">
              <label className="inline-flex justify-center items-center">
                <input
                  type="radio"
                  name="shingleType"
                  checked={shingleType === SHINGLE_TYPES.THREE_TAB}
                  onChange={() => setShingleType(SHINGLE_TYPES.THREE_TAB)}
                />
                <span className="ml-1">3-tab</span>
              </label>
              <label className="inline-flex justify-center items-center">
                <input
                  type="radio"
                  name="shingleType"
                  checked={shingleType === SHINGLE_TYPES.ARCHITECTURAL}
                  onChange={() => setShingleType(SHINGLE_TYPES.ARCHITECTURAL)}
                />
                <span className="ml-1">Architectural</span>
              </label>
            </div>
          </div>

          {shingleType && (
            <div className="mb-4">
              <h3 className="font-medium mb-3">Color</h3>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4 justify-items-center">
                {SHINGLE_COLORS.map((color) => (
                  <label key={color} className="inline-block cursor-pointer">
                    <input
                      type="radio"
                      name={`shingleColor${shingleType}`}
                      checked={shingleColor === color}
                      onChange={() => setShingleColor(color)}
                      className="mb-2"
                    />
                    <img
                      src={getColorImage(color, shingleType)}
                      alt={`Shingle color ${color} ${shingleType}`}
                      className="w-24 sm:w-32 md:w-40 rounded-3xl"
                    />
                    <span className="block text-center text-sm">{color}</span>
                  </label>
                ))}
              </div>

              <div className="mt-4">
                <label className="inline-block">
                  Other color:
                  <input
                    type="text"
                    value={shingleColorOther}
                    onChange={(e) => {
                      setShingleColor("other");
                      setField("shingleColorOther", e.target.value);
                    }}
                    placeholder="If you have a different color, type it here"
                    className="mt-2 ml-1 border-2 border-gray-300 rounded px-2 py-1 w-full md:w-96"
                  />
                </label>
              </div>
            </div>
          )}

          {shingleType && (
            <div className="relative inline-block text-left mt-4">
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setBrandDropdownOpen(!brandDropdownOpen)}
                  className="inline-flex w-auto justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-md font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 hover:cursor-pointer"
                >
                  {shingleBrand || "Brand"}
                  <svg
                    className="-mr-1 size-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <InfoTooltip>
                  Select the exact brand to ensure compatibility with other
                  structures or HOA requirements.
                </InfoTooltip>
              </div>

              {brandDropdownOpen && (
                <div className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5">
                  <div className="py-1">
                    {SHINGLE_BRANDS.map((brand) => (
                      <button
                        key={brand}
                        type="button"
                        onClick={() => {
                          setShingleBrand(brand);
                          setBrandDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:cursor-pointer"
                      >
                        {brand}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setShingleBrand("other");
                        setBrandDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:cursor-pointer"
                    >
                      Other
                    </button>
                  </div>
                </div>
              )}

              {shingleBrand === "other" && (
                <input
                  type="text"
                  value={shingleBrandOther}
                  onChange={(e) =>
                    setField("shingleBrandOther", e.target.value)
                  }
                  placeholder="Type the brand here"
                  className="border-2 border-gray-300 rounded mt-2 px-2 py-1 block"
                />
              )}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
